// CR161: Clickable AI Call ID component that deep-links to evidence
import { Button } from "@/components/ui/button";
import { ExternalLink, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";

interface AiCallLinkProps {
  aiCallId: string;
  pageNumber?: number;
  model?: string;
  durationMs?: number;
  rawOutput?: unknown;
  className?: string;
  showIcon?: boolean;
}

/**
 * Renders a clickable AI Call ID that opens a dialog with call details
 * CR161: Makes AI Call: <id> clickable to view evidence
 */
export function AiCallLink({
  aiCallId,
  pageNumber,
  model,
  durationMs,
  rawOutput,
  className = "",
  showIcon = true,
}: AiCallLinkProps) {
  const [open, setOpen] = useState(false);
  
  // Truncate ID for display
  const displayId = aiCallId.length > 12 
    ? `${aiCallId.substring(0, 8)}...` 
    : aiCallId;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="link"
          size="sm"
          className={`h-auto p-0 font-mono text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 ${className}`}
          title={`View AI call details: ${aiCallId}`}
        >
          {showIcon && <Zap className="h-3 w-3 mr-1" />}
          {displayId}
          <ExternalLink className="h-3 w-3 ml-1 opacity-50" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" />
          AI Call Evidence
        </DialogTitle>
          <DialogDescription>
            Details for AI extraction call
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="border rounded-lg p-3">
              <p className="text-muted-foreground text-xs">Call ID</p>
              <p className="font-mono text-xs break-all">{aiCallId}</p>
            </div>
            {pageNumber !== undefined && (
              <div className="border rounded-lg p-3">
                <p className="text-muted-foreground text-xs">Page Number</p>
                <p className="font-medium">{pageNumber}</p>
              </div>
            )}
            {model && (
              <div className="border rounded-lg p-3">
                <p className="text-muted-foreground text-xs">Model Used</p>
                <p className="font-medium text-xs">{model}</p>
              </div>
            )}
            {durationMs !== undefined && (
              <div className="border rounded-lg p-3">
                <p className="text-muted-foreground text-xs">Duration</p>
                <p className="font-medium">{(durationMs / 1000).toFixed(2)}s</p>
              </div>
            )}
          </div>
          
          {/* Raw Output JSON */}
          {rawOutput !== undefined && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium">Raw AI Output</h4>
                <Badge variant="outline" className="text-xs">JSON</Badge>
              </div>
              <ScrollArea className="h-[300px] border rounded-lg p-3 bg-muted/30">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {JSON.stringify(rawOutput, null, 2)}
                </pre>
              </ScrollArea>
            </div>
          )}
          
          {rawOutput === undefined && (
            <div className="text-center py-8 text-muted-foreground border rounded-lg">
              <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No raw output available for this call</p>
              <p className="text-xs mt-1">Output may not have been persisted</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
